import React from "react";
import { useParams } from "react-router-dom";

import { DropdownItem } from "react-bootstrap";
import { Button } from "../../../../components";
import ActionsModal, {
  ActionsModalNames
} from "../../../../components/ActionsModal";
import DropdownContainer from "../../../../components/dropdown/DropdownContainer";
import useModal from "../../../../hooks/useModal";
import {
  useDownloadDroppedFiles,
  useDownloadScreenshots,
  useGetRLSandboxResourcesStatus
} from "../../../samples/hooks";
import { useNetworkThreatIntelPayloads } from "../../hooks/useNetworkThreatIntelPayloads";

import styles from "./common.module.scss";

interface Props {
  sha1: string;
  analysisId: string;
}

const { rl_dynamic_analysis_enabled: rlcsEnabled } = window.tcbase.consts;

const lastPayloadQueryParams = {
  page_size: 1,
  page: 1,
  ordering: "-local_last_seen" // get last payload
};

const NetworkThreatActionsButton: React.FC<Props> = ({ sha1, analysisId }) => {
  const { url: _url = "" } = useParams();
  const [showLatestPayload, , toggleLatestPayload] = useModal();
  const [showActionMenu, , toggleActionMenu] = useModal(false);

  const { data: payloadsReport } = useNetworkThreatIntelPayloads(
    _url,
    lastPayloadQueryParams
  );
  const { downloadScreenshots } = useDownloadScreenshots(
    sha1,
    analysisId,
    true
  );
  const { downloadDroppedFiles } = useDownloadDroppedFiles(
    sha1,
    analysisId,
    true
  );
  const { data } = useGetRLSandboxResourcesStatus(sha1, analysisId, true, {
    enabled: Boolean(analysisId)
  });
  const actions = [
    {
      action: {
        label: "Download Latest Payload",
        disabled: !payloadsReport?.results?.[0]?.sha1,
        disabledMessage:
          "Payload not available. Reprocessing the file generates new artifacts.",
        onClick: toggleLatestPayload
      },
      show: Boolean(_url)
    },
    {
      action: {
        label: "Download Latest Screenshots",
        onClick: () => {
          downloadScreenshots();
        },
        disabled: !data?.has_screenshots,
        disabledMessage:
          "Screenshots no longer available. Reprocessing the file generates new artifacts."
      },
      show: Boolean(_url) && rlcsEnabled
    },
    {
      action: {
        label: "Download Latest Dropped Files",
        onClick: () => {
          downloadDroppedFiles();
        },
        disabled: !data?.has_dropped_files,
        disabledMessage:
          "Dropped files no longer available. Reprocessing the file generates new artifacts."
      },
      show: Boolean(_url) && rlcsEnabled
    }
  ]
    .filter(({ show }) => show)
    .map(({ action }) => action);

  return (
    <div className="summary-header__buttons">
      <DropdownContainer
        name="nti-actions"
        buttonClassName={styles.dropdown_button}
        id="nti-actions"
        autoClose="outside"
        buttonVariant="primary"
        show={showActionMenu}
        onToggle={toggleActionMenu}
        buttonLabel="ACTIONS"
        buttonSize="md"
        buttonComponent={Button}
      >
        {actions.map(action => (
          <DropdownItem
            key={action.label}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </DropdownItem>
        ))}
      </DropdownContainer>
      {showLatestPayload && (
        <ActionsModal
          modalName={ActionsModalNames.Download}
          activeModalData={{
            sha1: payloadsReport?.results?.[0]?.sha1,
            file_names: [_url ?? payloadsReport?.results?.[0].sha1]
          }}
          toggleModal={toggleLatestPayload}
        />
      )}
    </div>
  );
};

export default NetworkThreatActionsButton;
